<template>
  <!-- 编辑邮件账户模态框 -->
  <div v-if="visible" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <!-- 背景遮罩 -->
      <div 
        v-if="visible"
        class="fixed inset-0 transition-opacity"
        @click="handleClose"
      >
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <!-- 模态框内容 -->
      <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6 w-[90vw] max-w-[90vw] mx-auto">
        <!-- 头部 -->
        <div class="flex items-center justify-between mb-4">
          <div>
            <h3 class="text-lg font-semibold text-gray-900">编辑邮件账户</h3>
            <p class="text-sm text-gray-500 mt-1">修改账户的显示信息和分组</p>
          </div>
          <button
            @click="handleClose"
            class="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg p-1"
          >
            <XMarkIcon class="h-6 w-6" />
          </button>
        </div>

        <!-- Tab 导航 -->
        <div class="border-b border-gray-200">
          <nav class="flex space-x-8" aria-label="Tabs">
            <button
              v-for="tab in tabs"
              :key="tab.key"
              @click="activeTab = tab.key"
              :class="[
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm'
              ]"
            >
              {{ tab.label }}
            </button>
          </nav>
        </div>

        <!-- 表单内容 -->
        <div class="mt-4">
          <!-- 基本信息标签页 -->
          <div v-show="activeTab === 'basic'" class="space-y-4">
            <!-- 邮箱服务提供商（只读） -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                邮箱服务提供商 <span class="text-red-500">*</span>
              </label>
              <div class="bg-gray-50 px-3 py-2 rounded-lg border text-sm text-gray-600">
                {{ getProviderDisplayName(formData.provider) }}
              </div>
            </div>

            <!-- 所属分组 -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                所属分组
              </label>
              <SearchableSelect
                v-model="formData.group_id"
                :options="groupOptions"
                placeholder="请选择分组（可选）"
                :clearable="true"
                class="w-full h-10"
                style="height: 42px;"
                :disabled="loading"
              />
            </div>

            <!-- 邮箱地址（只读） -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                邮箱地址 <span class="text-red-500">*</span>
              </label>
              <div class="bg-gray-50 px-3 py-2 rounded-lg border text-sm text-gray-600">
                {{ formData.email }}
              </div>
            </div>

            <!-- 显示名称 -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                显示名称 <span class="text-red-500">*</span>
              </label>
              <input
                v-model="formData.display_name"
                type="text"
                placeholder="可选，用于显示的账户名称"
                class="w-full h-10 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 placeholder-gray-400"
                :disabled="loading"
              >
            </div>
          </div>

          <!-- 认证信息标签页 -->
          <div v-show="activeTab === 'auth'" class="space-y-6">
            <!-- 认证类型显示 -->
            <div class="bg-gray-50 rounded-lg p-4">
              <div class="flex items-center justify-between">
                <div>
                  <h4 class="text-sm font-medium text-gray-900">认证类型</h4>
                  <p class="text-sm text-gray-500 mt-1">
                    {{ formData.provider === 'outlook' ? 'OAuth2认证 (Microsoft)' : '密码认证 (IMAP)' }}
                  </p>
                </div>
                <span :class="[
                  'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                  formData.provider === 'outlook' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                ]">
                  {{ formData.provider === 'outlook' ? 'OAuth2' : '密码' }}
                </span>
              </div>
            </div>

            <!-- 密码认证区域 -->
            <div v-if="formData.provider !== 'outlook'" class="space-y-4">
              <div class="border border-gray-200 rounded-lg p-4">
                <h4 class="text-sm font-medium text-gray-900 mb-3 flex items-center">
                  <KeyIcon class="h-4 w-4 mr-2 text-gray-500" />
                  修改应用密码
                </h4>
                <div class="space-y-3">
                  <div class="relative">
                    <input
                      v-model="newPassword"
                      :type="showPassword ? 'text' : 'password'"
                      placeholder="输入新的应用密码（留空则不修改）"
                      class="w-full h-10 px-3 pr-10 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 placeholder-gray-400"
                      :disabled="updatingPassword"
                    />
                    <button
                      type="button"
                      @click="showPassword = !showPassword"
                      class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      <svg v-if="showPassword" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                      <svg v-else class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                  </div>
                  <p class="text-xs text-gray-500">
                    提示：如需修改密码，请输入新的应用密码；留空则保持原密码不变
                  </p>
                  <button
                    @click="handleUpdatePassword"
                    :disabled="!newPassword || updatingPassword"
                    class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg v-if="updatingPassword" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {{ updatingPassword ? '更新中...' : '更新密码' }}
                  </button>
                </div>
              </div>
            </div>

            <!-- OAuth2认证区域 -->
            <div v-else class="space-y-4">
              <div class="border border-gray-200 rounded-lg p-4">
                <h4 class="text-sm font-medium text-gray-900 mb-3 flex items-center">
                  <ArrowPathIcon class="h-4 w-4 mr-2 text-gray-500" />
                  OAuth2 授权状态
                </h4>
                
                <!-- 授权状态显示 -->
                <div v-if="authStatus" class="mb-4">
                  <div :class="[
                    'flex items-center p-3 rounded-lg',
                    authStatus.oauth_status?.valid ? 'bg-green-50' : 'bg-yellow-50'
                  ]">
                    <CheckCircleIcon v-if="authStatus.oauth_status?.valid" class="h-5 w-5 text-green-500 mr-2" />
                    <ExclamationTriangleIcon v-else class="h-5 w-5 text-yellow-500 mr-2" />
                    <div>
                      <p :class="authStatus.oauth_status?.valid ? 'text-green-800' : 'text-yellow-800'" class="text-sm font-medium">
                        {{ authStatus.oauth_status?.message || '未知状态' }}
                      </p>
                      <p v-if="authStatus.oauth_status?.expires_at" class="text-xs text-gray-500 mt-1">
                        令牌过期时间: {{ formatDate(authStatus.oauth_status.expires_at) }}
                      </p>
                    </div>
                  </div>
                </div>
                
                <button
                  @click="handleReauthorize"
                  :disabled="reauthorizing"
                  class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg v-if="reauthorizing" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <ArrowPathIcon v-else class="h-4 w-4 mr-2" />
                  {{ reauthorizing ? '正在获取授权链接...' : '重新授权' }}
                </button>
              </div>
            </div>

            <!-- 测试连接区域 -->
            <div class="border border-gray-200 rounded-lg p-4">
              <h4 class="text-sm font-medium text-gray-900 mb-3">测试连接</h4>
              
              <!-- 连接测试结果 -->
              <div v-if="connectionTestResult" class="mb-4 space-y-2">
                <div :class="[
                  'flex items-center p-2 rounded',
                  connectionTestResult.results?.imap?.success ? 'bg-green-50' : 'bg-red-50'
                ]">
                  <CheckCircleIcon v-if="connectionTestResult.results?.imap?.success" class="h-4 w-4 text-green-500 mr-2" />
                  <XCircleIcon v-else class="h-4 w-4 text-red-500 mr-2" />
                  <span :class="connectionTestResult.results?.imap?.success ? 'text-green-700' : 'text-red-700'" class="text-sm">
                    IMAP: {{ connectionTestResult.results?.imap?.message }}
                  </span>
                </div>
                <div :class="[
                  'flex items-center p-2 rounded',
                  connectionTestResult.results?.smtp?.success ? 'bg-green-50' : 'bg-red-50'
                ]">
                  <CheckCircleIcon v-if="connectionTestResult.results?.smtp?.success" class="h-4 w-4 text-green-500 mr-2" />
                  <XCircleIcon v-else class="h-4 w-4 text-red-500 mr-2" />
                  <span :class="connectionTestResult.results?.smtp?.success ? 'text-green-700' : 'text-red-700'" class="text-sm">
                    SMTP: {{ connectionTestResult.results?.smtp?.message }}
                  </span>
                </div>
              </div>
              
              <button
                @click="handleTestConnection"
                :disabled="testingConnection"
                class="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg v-if="testingConnection" class="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ testingConnection ? '测试中...' : '测试连接' }}
              </button>
            </div>
          </div>

          <!-- 自定义字段标签页 -->
          <div v-show="activeTab === 'custom'" class="space-y-4">
            <div v-if="customFields.length === 0" class="text-center py-8 text-gray-500">
              暂无自定义字段
            </div>
            
            <div v-else class="space-y-4">
              <div v-for="field in customFields" :key="field.id">
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  {{ field.label }}
                  <span v-if="field.is_required" class="text-red-500">*</span>
                </label>
                
                <!-- 文本输入 -->
                <input
                  v-if="field.field_type === 'text'"
                  v-model="customFieldValues[field.id]"
                  type="text"
                  :placeholder="field.placeholder"
                  :required="field.is_required"
                  class="w-full h-10 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 placeholder-gray-400"
                  :disabled="loading"
                />
                
                <!-- 多行文本 -->
                <textarea
                  v-else-if="field.field_type === 'textarea'"
                  v-model="customFieldValues[field.id]"
                  :placeholder="field.placeholder"
                  :required="field.is_required"
                  rows="3"
                  class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 placeholder-gray-400"
                  :disabled="loading"
                ></textarea>
                
                <!-- 数字输入 -->
                <input
                  v-else-if="field.field_type === 'number'"
                  v-model.number="customFieldValues[field.id]"
                  type="number"
                  :placeholder="field.placeholder"
                  :required="field.is_required"
                  class="w-full h-10 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 placeholder-gray-400"
                  :disabled="loading"
                />

                <!-- 小数输入 -->
                <input
                  v-else-if="field.field_type === 'decimal'"
                  v-model.number="customFieldValues[field.id]"
                  type="number"
                  step="0.01"
                  :placeholder="field.placeholder"
                  :required="field.is_required"
                  class="w-full h-10 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 placeholder-gray-400"
                  :disabled="loading"
                />

                <!-- 日期输入 -->
                <input
                  v-else-if="field.field_type === 'date'"
                  v-model="customFieldValues[field.id]"
                  type="date"
                  :required="field.is_required"
                  class="w-full h-10 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200"
                  :disabled="loading"
                />

                <!-- 布尔值 -->
                <div v-else-if="field.field_type === 'boolean'" class="flex items-center">
                  <input
                    v-model="customFieldValues[field.id]"
                    type="checkbox"
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    :disabled="loading"
                  />
                  <span class="ml-2 text-sm text-gray-600">{{ field.help_text || '是' }}</span>
                </div>

                <!-- 网址输入 -->
                <input
                  v-else-if="field.field_type === 'url'"
                  v-model="customFieldValues[field.id]"
                  type="url"
                  :placeholder="field.placeholder"
                  :required="field.is_required"
                  class="w-full h-10 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 placeholder-gray-400"
                  :disabled="loading"
                />
                
                <!-- 邮箱输入 -->
                <input
                  v-else-if="field.field_type === 'email'"
                  v-model="customFieldValues[field.id]"
                  type="email"
                  :placeholder="field.placeholder"
                  :required="field.is_required"
                  class="w-full h-10 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 placeholder-gray-400"
                  :disabled="loading"
                />
                
                <!-- 单选 -->
                <select
                  v-else-if="field.field_type === 'choice'"
                  v-model="customFieldValues[field.id]"
                  :required="field.is_required"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  :disabled="loading"
                >
                  <option value="">请选择</option>
                  <option v-for="choice in (field.choices || [])" :key="choice.value" :value="choice.value">
                    {{ choice.label }}
                  </option>
                </select>
                
                <!-- 多选 -->
                <div v-else-if="field.field_type === 'multiple_choice'" class="space-y-2">
                  <label v-for="choice in (field.choices || [])" :key="choice.value" class="flex items-center">
                    <input
                      v-model="customFieldValues[field.id]"
                      type="checkbox"
                      :value="choice.value"
                      class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      :disabled="loading"
                    />
                    <span class="ml-2 text-sm text-gray-700">{{ choice.label }}</span>
                  </label>
                </div>

                <!-- 帮助文本 -->
                <p v-if="field.help_text" class="mt-1 text-xs text-gray-500">
                  {{ field.help_text }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- 底部按钮 -->
        <div class="flex items-center justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
          <button
            @click="handleClose"
            :disabled="loading"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            取消
          </button>
          <button
            @click="handleSubmit"
            :disabled="loading || !formData.display_name"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 flex items-center space-x-2"
          >
            <svg v-if="loading" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>{{ loading ? '保存中...' : '保存修改' }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import { XMarkIcon, KeyIcon, ArrowPathIcon, CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/outline'
import emailAccountService from '@/services/emailAccountService'
import SearchableSelect from '@/components/common/SearchableSelect.vue'

const toast = useToast()

// Props
const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  account: {
    type: Object,
    default: null
  },
  groups: {
    type: Array,
    default: () => []
  },
  customFieldsUpdateTrigger: {
    type: Number,
    default: 0
  }
})

// Emits
const emit = defineEmits(['close', 'success'])

// 响应式数据
const loading = ref(false)
const activeTab = ref('basic')
const customFields = ref([])
const formData = ref({
  email: '',
  provider: '',
  display_name: '',
  group_id: null
})
const customFieldValues = ref({})

// 认证相关状态
const newPassword = ref('')
const showPassword = ref(false)
const updatingPassword = ref(false)
const testingConnection = ref(false)
const reauthorizing = ref(false)
const authStatus = ref(null)
const connectionTestResult = ref(null)

// 计算属性
const hasCustomFields = computed(() => {
  return customFields.value.length > 0
})

const tabs = computed(() => {
  const tabList = [
    { key: 'basic', label: '基本信息' },
    { key: 'auth', label: '认证信息' }
  ]
  
  if (hasCustomFields.value) {
    tabList.push({ key: 'custom', label: '自定义字段' })
  }
  
  return tabList
})

const groupOptions = computed(() => {
  return [
    { value: null, label: '无分组' },
    ...props.groups.map(group => ({
      value: group.id,
      label: group.name,
      description: group.description
    }))
  ]
})

// 方法
const getProviderDisplayName = (provider) => {
  const providerMap = {
    'outlook': 'Outlook',
    'gmail': 'Gmail',
    '163': '163邮箱',
    '163vip': '163VIP邮箱',
    'aliyun': '阿里云企业邮箱'
  }
  return providerMap[provider] || provider
}

const loadCustomFields = async () => {
  try {
    const response = await emailAccountService.getCustomFields({ is_active: true })
    if (response.success) {
      customFields.value = response.data.results || []
    }
  } catch (error) {
    console.error('加载自定义字段失败:', error)
  }
}

const resetForm = () => {
  if (props.account) {
    formData.value = {
      email: props.account.email || '',
      provider: props.account.provider || '',
      display_name: props.account.display_name || '',
      group_id: props.account.group?.id || null
    }
    
    // 重置自定义字段值
    customFieldValues.value = {}
    if (props.account.custom_fields && Array.isArray(props.account.custom_fields)) {
      props.account.custom_fields.forEach(fieldValue => {
        if (fieldValue.field_type === 'multiple_choice') {
          // 多选字段需要转换为数组
          try {
            customFieldValues.value[fieldValue.field_id] = Array.isArray(fieldValue.value) 
              ? fieldValue.value 
              : JSON.parse(fieldValue.value || '[]')
          } catch (e) {
            customFieldValues.value[fieldValue.field_id] = []
          }
        } else {
          customFieldValues.value[fieldValue.field_id] = fieldValue.value
        }
      })
    }
  }
  
  // 重置到第一个标签页
  activeTab.value = 'basic'
}

const collectCustomFieldData = () => {
  const customFieldData = []
  
  customFields.value.forEach(field => {
    const value = customFieldValues.value[field.id]
    let processedValue = value
    
    // 处理不同字段类型的值
    if (value === undefined || value === null) {
      processedValue = ''  // 将 undefined 和 null 转换为空字符串
    } else if (field.field_type === 'multiple_choice' && Array.isArray(value)) {
      processedValue = JSON.stringify(value)
    } else if (field.field_type === 'multiple_choice' && !Array.isArray(value)) {
      processedValue = JSON.stringify([])  // 确保多选字段为空数组的JSON
    }
    
    // 包含所有字段，包括空值字段，这样后端知道要清空这些字段
    customFieldData.push({
      field_id: field.id,
      value: processedValue
    })
  })
  
  return customFieldData
}

// 验证自定义字段
const validateCustomFields = () => {
  for (const field of customFields.value) {
    if (field.is_required) {
      const value = customFieldValues.value[field.id]
      
      // 检查是否为空值
      if (value === undefined || value === null || value === '') {
        return `请填写必填字段: ${field.label}`
      }
      
      // 检查多选字段是否为空数组
      if (field.field_type === 'multiple_choice' && Array.isArray(value) && value.length === 0) {
        return `请选择必填字段: ${field.label}`
      }
    }
  }
  return null
}

const handleSubmit = async () => {
  if (!formData.value.display_name) {
    toast.error('请输入显示名称')
    return
  }
  
  // 验证自定义字段
  const customFieldError = validateCustomFields()
  if (customFieldError) {
    toast.error(customFieldError)
    return
  }
  
  loading.value = true
  
  try {
    const payload = {
      email: formData.value.email,
      provider: formData.value.provider,
      display_name: formData.value.display_name,
      group_id: formData.value.group_id
    }
    
    // 添加自定义字段数据（包括空值字段，确保后端能正确清空字段）
    const customFieldData = collectCustomFieldData()
    if (customFields.value.length > 0) {
      payload.custom_field_values = customFieldData
    }
    
    const result = await emailAccountService.updateEmailAccount(props.account.id, payload)
    
    if (result.success) {
      emit('success', result.data)
    } else {
      toast.error(result.message || '更新账户失败')
    }
  } catch (error) {
    console.error('更新邮件账户失败:', error)
    toast.error(error.response?.data?.message || '更新账户失败')
  } finally {
    loading.value = false
  }
}

const handleClose = () => {
  if (!loading.value) {
    emit('close')
  }
}

// 格式化日期
const formatDate = (dateString) => {
  if (!dateString) return ''
  try {
    const date = new Date(dateString)
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch (e) {
    return dateString
  }
}

// 加载认证状态
const loadAuthStatus = async () => {
  if (!props.account?.id) return
  
  try {
    const response = await emailAccountService.getAuthStatus(props.account.id)
    if (response.success) {
      authStatus.value = response.data
    }
  } catch (error) {
    console.error('加载认证状态失败:', error)
  }
}

// 更新密码
const handleUpdatePassword = async () => {
  if (!newPassword.value) {
    toast.error('请输入新密码')
    return
  }
  
  updatingPassword.value = true
  
  try {
    const response = await emailAccountService.updatePassword(props.account.id, newPassword.value)
    
    if (response.success) {
      toast.success(response.message || '密码已更新')
      newPassword.value = ''
      
      // 如果连接测试失败，显示警告
      if (response.connection_test && !response.connection_test.success) {
        toast.warning(response.connection_test.message)
      }
    } else {
      toast.error(response.message || '更新密码失败')
    }
  } catch (error) {
    console.error('更新密码失败:', error)
    toast.error(error.response?.data?.message || '更新密码失败')
  } finally {
    updatingPassword.value = false
  }
}

// 测试连接
const handleTestConnection = async () => {
  testingConnection.value = true
  connectionTestResult.value = null
  
  try {
    const response = await emailAccountService.testConnection(props.account.id)
    connectionTestResult.value = response
    
    if (response.success) {
      toast.success('连接测试成功')
    } else {
      toast.warning('部分连接测试失败')
    }
  } catch (error) {
    console.error('测试连接失败:', error)
    connectionTestResult.value = {
      success: false,
      results: {
        imap: { success: false, message: error.response?.data?.message || '连接测试失败' },
        smtp: { success: false, message: '未测试' }
      }
    }
    toast.error('连接测试失败')
  } finally {
    testingConnection.value = false
  }
}

// 重新授权
const handleReauthorize = async () => {
  reauthorizing.value = true
  
  try {
    const response = await emailAccountService.reauthorize(props.account.id)
    
    if (response.success && response.auth_url) {
      // 打开授权窗口
      const authWindow = window.open(response.auth_url, '_blank', 'width=600,height=700')
      
      toast.info(response.message || '请在新窗口中完成授权')
      
      // 监听授权完成（可选：通过轮询或消息事件）
      const checkInterval = setInterval(async () => {
        if (authWindow?.closed) {
          clearInterval(checkInterval)
          // 重新加载认证状态
          await loadAuthStatus()
          toast.success('授权窗口已关闭，正在刷新状态...')
        }
      }, 1000)
      
      // 30秒后停止检查
      setTimeout(() => {
        clearInterval(checkInterval)
      }, 30000)
      
    } else {
      toast.error(response.message || '获取授权链接失败')
    }
  } catch (error) {
    console.error('重新授权失败:', error)
    toast.error(error.response?.data?.message || '重新授权失败')
  } finally {
    reauthorizing.value = false
  }
}

// 监听弹窗显示状态
watch(() => props.visible, async (newVal) => {
  if (newVal) {
    resetForm()
    // 重置认证相关状态
    newPassword.value = ''
    showPassword.value = false
    connectionTestResult.value = null
    authStatus.value = null
    
    await loadCustomFields()
    
    // 如果是OAuth账户，加载认证状态
    if (props.account?.provider === 'outlook') {
      await loadAuthStatus()
    }
  }
})

// 监听自定义字段更新触发器
watch(() => props.customFieldsUpdateTrigger, async () => {
  if (props.visible) {
    await loadCustomFields()
  }
})

// 组件挂载时加载自定义字段
onMounted(() => {
  if (props.visible) {
    loadCustomFields()
  }
})
</script>
